/* eslint-disable react/prop-types */
import React, { useState } from "react";
import { useQuery, useApolloClient } from "@apollo/client";
import { Button, Control, Field, Label, Select } from "rbx";
import PageHeader from "../../components/PageHeader";
import "./WPQSearch.scss";
import { GET_WPQ_PROCESSES, GET_WPQ_EMPLOYEES, GET_WPQ_ROWS_BY_EMPLOYEE, GET_WPQ_PDF, GET_WPQ_PROCEDURES, GET_WPQ_ROWS, GET_WPS_NAMES } from "../../graphql";
import ReactSelect from "../../components/ReactSelect";
import { Table } from "../../components/DataTable";
import { downloadPdfFromDataUrl } from "../../utils";

const WPQSearch = () => {
    const [process, setProcess] = useState("");
    const [procedure, setProcedure] = useState("");
    const [partners, setPartners] = useState([]);
    const { data: proceduresData } = useQuery(GET_WPQ_PROCEDURES, { fetchPolicy: "cache-first" });
    const { data: wpsNamesData } = useQuery(GET_WPS_NAMES, { fetchPolicy: "cache-first" });
    const { data } = useQuery(GET_WPQ_PROCESSES, { fetchPolicy: "cache-first" });
    const { data: employeesData } = useQuery(GET_WPQ_EMPLOYEES, { fetchPolicy: "cache-first" });
    const [rows, setRows] = useState([]);
    const apolloClient = useApolloClient();

    const handleSearch = async (e) => {
        e.preventDefault();
        try {
            if (partners?.length) {
                const results = await Promise.all(
                    partners.map((p) =>
                        apolloClient.query({
                            query: GET_WPQ_ROWS_BY_EMPLOYEE,
                            variables: { employeeId: p.id },
                            fetchPolicy: "network-only",
                        })
                    )
                );
                const merged = results.flatMap((r) => r?.data?.getWpqRowsByEmployee || []);
                setRows(merged);
                return;
            }

            // Fallback to process/procedure search
            if (process || (Array.isArray(procedure) && procedure.length > 0)) {
                const [procType, appMethodRaw] = process ? process.split("|") : [undefined, undefined];
                const applicationMethod = appMethodRaw
                    ? appMethodRaw.replace("(", "").replace(")", "")
                    : undefined;
                const res = await apolloClient.query({
                    query: GET_WPQ_ROWS,
                    variables: {
                        processType: procType,
                        applicationMethod,
                        procedures: Array.isArray(procedure) ? procedure : [],
                    },
                    fetchPolicy: "network-only",
                });
                setRows(res?.data?.getWpqRows || []);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    };

    const partnerOptions = (employeesData?.getWpqEmployees || []).map((p) => ({
        value: { id: p.id, externalId: p.externalId },
        label: p.label,
    }));

    const handlePartnerChange = (selected) => {
        if (Array.isArray(selected)) {
            setPartners(selected.map((o) => o.value));
        } else {
            setPartners(selected ? [selected.value] : []);
        }
    };

    const ActionCell = ({ row: rowProp }) => {
        const { externalId } = rowProp.original || {};
        return (
            <Button
                color="primary"
                onClick={async () => {
                    try {
                        const res = await apolloClient.query({
                            query: GET_WPQ_PDF,
                            variables: { externalId },
                            fetchPolicy: "network-only",
                        });
                        const dataUrl = res?.data?.getWpqPdf;
                        if (dataUrl) {
                            await downloadPdfFromDataUrl(dataUrl, `WPQ_${externalId}.pdf`);
                        }
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.error(e);
                    }
                }}
            >
                Download
            </Button>
        );
    };

    return (
        <div className="wpq-search-container">
            <PageHeader title="WPQ Search" />

            <form className="wpq-search-form-wrapper" onSubmit={handleSearch}>
                <Field>
                    <Label>WPQ Process:</Label>
                    <Control>
                        <Select.Container fullwidth>
                            <Select value={process} onChange={(e) => setProcess(e.target.value)}>
                                <Select.Option value="">Select process</Select.Option>
                                {data?.getWpqProcesses?.map((p) => (
                                    <Select.Option
                                        key={`${p.type}-${p.applicationMethod}`}
                                        value={`${p.type}|${p.applicationMethod}`}
                                    >
                                        {p.type} ({p.applicationMethod})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Select.Container>
                    </Control>
                </Field>

                <Field>
                    <Label>WPS:</Label>
                    <Control>
                        <ReactSelect
                            closeMenuOnSelect
                            isMulti
                            name="procedure"
                            options={(wpsNamesData?.getWpsNames || []).map((name) => ({ label: name, value: name }))}
                            value={(wpsNamesData?.getWpsNames || [])
                                .filter((name) => (Array.isArray(procedure) ? procedure.includes(name) : false))
                                .map((name) => ({ label: name, value: name }))}
                            onChange={(selected) => setProcedure(Array.isArray(selected) ? selected.map((o) => o.value) : [])}
                        />
                    </Control>
                </Field>

                <div className="wpq-or-divider"> - OR - </div>

                <Field>
                    <Label>Partners:</Label>
                    <Control>
                        <ReactSelect
                            closeMenuOnSelect
                            isMulti
                            name="partner"
                            options={partnerOptions}
                            value={partnerOptions.filter((o) => partners?.some((p) => p.id === o.value.id))}
                            onChange={handlePartnerChange}
                        />
                    </Control>
                </Field>

                <Field>
                    <Control>
                        <Button color="success" type="submit">Search</Button>
                    </Control>
                </Field>
            </form>

            {rows?.length > 0 && (
                <div style={{ marginTop: "1.5rem" }}>
                    <Table
                        columns={[
                            { Header: "Partner's Name", accessor: "partnerName" },
                            { Header: "Process Title", accessor: "processTitle" },
                            { Header: "Process Expires", accessor: "processExpires" },
                            { Header: "Procedure", accessor: "procedure" },
                            { Header: "WPQ Test Date", accessor: "wpqTestDate" },
                            { Header: "Action", id: "action", Cell: ActionCell },
                        ]}
                        data={rows}
                        onRowClick={() => { }}
                    />
                </div>
            )}
        </div>
    );
};

export default WPQSearch;


